const STORAGE_KEY = 'gymgo.pending-payment.v1';

// Only the payment signature travels into storage. No actor credentials,
// cookies or tokens are ever part of it, even if the caller passes extra
// fields on `payload` by mistake.
const SIGNATURE_FIELDS = ['membershipId', 'amount', 'date', 'method'];

// Exactly the format `POST /payments` accepts for a string amount (whole
// pesos, optionally with 1–2 decimal cents) — shared with `PaymentForm`'s own
// validation so the client never rejects/accepts a format the server
// wouldn't, and reused here to decide what's safe to canonicalize at all.
export const AMOUNT_PATTERN = /^\d+(?:\.\d{1,2})?$/;

/**
 * `amount` can legitimately arrive as either a number (a caller that already
 * parsed one) or a string (what an `<input>` always hands a form) — '9000'
 * and 9000 must count as the SAME signature, or a retry rebuilt from fresh
 * form state would look like a different attempt from the one originally
 * persisted (final review I4). Canonicalizing to the shortest round-trip
 * decimal string (`String(Number(value))`) at write time means every later
 * `===` comparison in `sameSignature` just works without special-casing
 * amount.
 *
 * Fix round 1 (opus review, item 2): a bare `Number(value)` also accepts
 * hex (`'0x10'`), exponential (`'1e3'`) and leading-dot (`'.5'`) notation —
 * none of them a format a real amount input ever produces, and each one
 * canonicalizing to a *different* number than it looks like would let two
 * textually-different, non-`AMOUNT_PATTERN` strings collide or diverge in
 * ways nothing here intends. Only a string that already matches the exact
 * server-accepted `AMOUNT_PATTERN` gets parsed; anything else (a plain
 * number, or a non-matching string — missing, empty, garbage, hex,
 * exponential) is left untouched so the "required field" check below still
 * sees it as missing/invalid instead of a masking canonicalized value.
 */
function canonicalAmount(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : value;
  if (typeof value === 'string' && AMOUNT_PATTERN.test(value)) return String(Number(value));
  return value;
}

/**
 * `window.localStorage` itself can throw `SecurityError` just from being
 * READ (third-party storage blocked, sandboxed iframe, some privacy modes) —
 * not only from `getItem`/`setItem` calls on it. Callers use this instead of
 * touching `window.localStorage` directly so that access never crashes a
 * render; every store operation already treats a missing/unusable `storage`
 * as "fail closed" (see `readAll`/`writeAll` below).
 */
export function getDefaultLocalStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}

function sanitizePayload(payload) {
  const clean = {};
  SIGNATURE_FIELDS.forEach((field) => {
    const raw = payload ? payload[field] : undefined;
    clean[field] = field === 'amount' ? canonicalAmount(raw) : raw;
  });
  return clean;
}

function sameSignature(a, b) {
  return SIGNATURE_FIELDS.every(field => a[field] === b[field]);
}

function isValidRecord(record) {
  return Boolean(
    record
    && typeof record === 'object'
    && typeof record.idempotencyKey === 'string' && record.idempotencyKey
    && record.payload
    && typeof record.payload.membershipId === 'string' && record.payload.membershipId,
  );
}

function readAll(storage) {
  let raw = null;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    return [];
  }
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isValidRecord) : [];
  } catch {
    // Corrupted JSON is discarded safely rather than crashing the app.
    return [];
  }
}

/**
 * `setItem`/`removeItem` can throw (quota exceeded, Safari private mode,
 * storage disabled by policy) — this is money-critical, so a write failure
 * must never look like a success. `begin` lets this propagate (fail closed:
 * the caller must not treat an unpersisted attempt as safe to send), while
 * `resolve` below swallows it deliberately so a definitive server answer can
 * never leave the UI stuck on "pending" just because the local cleanup wasn't
 * writable.
 */
function writeAll(storage, records) {
  try {
    if (records.length === 0) {
      // Nothing pending: drop the key entirely instead of leaving "[]".
      storage.removeItem(STORAGE_KEY);
    } else {
      storage.setItem(STORAGE_KEY, JSON.stringify(records));
    }
  } catch (cause) {
    const error = new Error('No se pudo guardar el intento de cobro de forma segura.');
    error.code = 'storage_unavailable';
    error.cause = cause;
    throw error;
  }
}

/**
 * Idempotent pending-payment store: at most one pending charge attempt per
 * membership, regardless of which admin is logged in on this browser. This
 * covers the case where a charge is confirmed server-side but the response
 * is lost (network drop, reload) — the UI must block a duplicate charge and
 * offer only a retry of the exact original signature+UUID until a
 * definitive result resolves it.
 */
export function createPendingPaymentStore(storage) {
  return {
    begin({ actorId, payload, idempotencyKey }) {
      if (!actorId) throw new Error('actorId es obligatorio');
      if (!idempotencyKey) throw new Error('idempotencyKey es obligatorio');
      const cleanPayload = sanitizePayload(payload);
      SIGNATURE_FIELDS.forEach((field) => {
        const value = cleanPayload[field];
        if (value === undefined || value === null || value === '') {
          // A record missing part of its signature could never be retried
          // "exactly" (Task 4 review, note c), so it is never stored.
          throw new Error(`payload.${field} es obligatorio`);
        }
      });

      const records = readAll(storage);

      // The same UUID must never end up covering two different membership
      // attempts (final review I4d) — that would let a retry payload for one
      // membership get silently treated as the resolution of another.
      const keyUsedElsewhere = records.find(
        record => record.idempotencyKey === idempotencyKey && record.payload.membershipId !== cleanPayload.membershipId,
      );
      if (keyUsedElsewhere) {
        throw new Error('Esa clave de idempotencia ya se usó para otra membresía.');
      }

      const existing = records.find(record => record.payload.membershipId === cleanPayload.membershipId);
      if (existing) {
        const isExactRetry = existing.idempotencyKey === idempotencyKey && sameSignature(existing.payload, cleanPayload);
        if (!isExactRetry) {
          throw new Error('Ya hay un cobro pendiente para esta membresía; sólo se puede reintentar el intento original.');
        }
        return existing;
      }

      const record = { actorId, payload: cleanPayload, idempotencyKey, createdAt: new Date().toISOString() };
      // If this throws (storage_unavailable), the record was never persisted
      // — the caller must treat `begin` as failed and never send the charge.
      writeAll(storage, [...records, record]);
      return record;
    },

    forMembership(membershipId) {
      return readAll(storage).find(record => record.payload.membershipId === membershipId) || null;
    },

    list() {
      return readAll(storage);
    },

    resolve(idempotencyKey) {
      const remaining = readAll(storage).filter(record => record.idempotencyKey !== idempotencyKey);
      try {
        writeAll(storage, remaining);
      } catch {
        // The payment is already server-confirmed either way; failing to
        // clear the local record must never leave the UI stuck blocking a
        // new charge forever. Worst case a resolved charge still shows as
        // pending until storage becomes writable again — never a blocked or
        // duplicated one.
      }
    },
  };
}
